import {EventEmitter} from 'events';

const EVENT_MOUSE_MOVE = 'mousemove';
const EVENT_TOUCH_START = 'touchstart';
const EVENT_TOUCH_MOVE = 'touchmove';
const EVENT_TOUCH_END = 'touchend';
const EVENT_WHEEL = 'wheel';

export const EMIT_UPDATED_CHANGED = 'updated';
export const EMIT_ZOOM_ENABLED = 'zoom-enabled';
export const EMIT_ZOOM_DISABLED = 'zoom-disabled';

export class Zoom extends EventEmitter {

    constructor(containerSelector, options) {
        super();
        /** @type {HTMLElement} */
        this.container = typeof containerSelector === 'string' ? document.querySelector(containerSelector): containerSelector;
        this.containerZoom = null;
        this.pctPositionX = 50;
        this.pctPositionY = 50;

        /**
         * @type {{zoomMax: number, levelQuality: null, regexReplace: {RegExp}, zoom: number, zoomMin: number }} this.options
         */
        this.options = {
            zoomMin: 1,
            zoom: 1,
            zoomMax: 5,
            regexReplace: null,
            levelQuality: null,
            inverted: false,
            deltaMove: 0.3
        };

        this.options = Object.assign(this.options, options);

        this._currentZoom = this.options.zoom;

        [EVENT_WHEEL, EVENT_TOUCH_MOVE].forEach(eventName => {
            this.container.addEventListener(eventName, event => {
                if (this.containerZoom === null && (event.type === EVENT_WHEEL || event.touches && event.touches.length === 2)) {
                    event.preventDefault();
                    if (event.deltaY > 0) {
                        return;
                    }
                    this.enableZoom();
                } else if (this.containerZoom && (event.type === EVENT_WHEEL || event.touches && event.touches.length === 2)) {
                    const eventTouch = new TouchEvent(event.type, {
                        changedTouches: event.changedTouches,
                        targetTouches: event.targetTouches,
                        touches: event.touches
                    });
                    this.containerZoom.dispatchEvent(eventTouch);
                }
            });
        });
    }

    /**
     * enable zoom and replace the container
     */
    enableZoom() {
        this._initContainer();
        this.container.replaceWith(this.containerZoom);
        this._update();
        this.emit(EMIT_ZOOM_ENABLED, this.containerZoom, this.container);
    }

    /**
     * Disable zoom, remove container
     */
    disableZoom() {
        this.containerZoom.replaceWith(this.container);
        this.containerZoom.remove();
        this.containerZoom = null;
        this.emit(EMIT_ZOOM_DISABLED, this.containerZoom, this.container);
    }

    /**
     * define size of zoom. If zoom equal to min, the zoom module is disable.
     * @param number
     */
    setZoom(number) {
        if (!this.containerZoom) {
            this.enableZoom();
        }
        this._currentZoom = Math.min(Math.max(this.options.zoomMin, number), this.options.zoomMax);
        this._update();

        if (this._currentZoom === this.options.zoomMin) {
            this.disableZoom(this.containerZoom, this.container);
        }
    }

    /**
     * Init the container for picture show in zoom
     * @private
     */
    _initContainer() {
        this.containerZoom = document.createElement('div');
        this.containerZoom.style.overflow = 'hidden';
        this.containerZoom.classList.add(...this.container.classList);
        this.containerZoom.style.height = this.container.clientHeight + 'px';
        this.containerZoom.style.width = this.container.clientWidth + 'px';
        this.containerZoom.style.backgroundImage = 'url(' + this.container.src + ')';
        this.containerZoom.style.backgroundRepeat = 'no-repeat';

        this.containerZoom.currentZoom = this.options.zoom;

        this.containerZoom.addEventListener(EVENT_WHEEL, event => {
            event.preventDefault();
            this._zoomMoveEvent(event);
            this.setZoom(this._currentZoom + (event.deltaY * -0.01));
        });

        this.containerZoom.addEventListener(EVENT_MOUSE_MOVE, event => {
            event.preventDefault();
            this._zoomMoveEvent(event);
        });

        this.containerZoom.addEventListener(EVENT_TOUCH_START, event => {
            event.preventDefault();
            this.positionX = event.touches[0].pageX;
            this.positionY = event.touches[0].pageY;
        });
        this.containerZoom.addEventListener(EVENT_TOUCH_MOVE, event => {
            event.preventDefault();
            if (event.touches.length === 1 && !this.pinchMode) {
                this._zoomMoveEventMobile(event);
                this.lastZoomPinch = -1;
                this.pinchMode = false;
            } else if (event.touches.length === 2) {
                this.pinchMode = true;
                this._zoomPinchEvent(event);
            }
        });
        this.containerZoom.addEventListener(EVENT_TOUCH_END, event => {
            event.preventDefault();
            this.positionX = null;
            this.positionY = null;
            this.pinchMode = false;
            this.lastZoomPinch = -1;
        });

        this.sizeContainer = Math.sqrt(
            Math.pow(this.container.clientWidth, 2) +
            Math.pow(this.container.clientHeight, 2)
        );
    }

    /**
     * @type {HTMLElement} container
     * @private
     */
    _update() {
        this._updatePicture();
        this.containerZoom.style.backgroundSize = (this._currentZoom * 100) + '%';
        this.containerZoom.style.backgroundPosition = Math.min(100, Math.max(0, this.pctPositionX)) + '% ' + Math.min(100, Math.max(0, this.pctPositionY)) + '%';

        this.emit(EMIT_UPDATED_CHANGED);
    }

    /**
     * Change picture according to the zoom
     * @private
     */
    _updatePicture() {
        if (!this.options.levelQuality || !this.options.regexReplace) {
            return;
        }
        let picturePath = this.containerZoom.style.backgroundImage;
        let levelPicture = null;
        for (const indexZoom of Object.keys(this.options.levelQuality).sort()) {
            if (this._currentZoom >= indexZoom) {
                levelPicture = this.options.levelQuality[indexZoom];
            }
        }

        picturePath = picturePath.replace(this.options.regexReplace, levelPicture);
        let image = new Image();
        image.src = /url\("(.*)"\)/.exec(picturePath)[1];
        image.onload = () => {
            if (this.containerZoom) {
                this.containerZoom.style.backgroundImage = picturePath;
            }
        };
    }

    /**
     * @type {HTMLElement} container
     * @type {MouseEvent|TouchEvent} event
     * @private
     */
    _zoomMoveEvent(event) {
        const offsetX = event.offsetX ? event.offsetX : event.touches[0].pageX;
        const offsetY = event.offsetY ? event.offsetY : event.touches[0].pageY;
        this._setCenterPicture(offsetX, offsetY);
        this._update();
    }

    _setCenterPicture(offsetX, offsetY) {
        if (this.containerZoom) {
            this.pctPositionX = offsetX / this.containerZoom.offsetWidth * 100;
            this.pctPositionY = offsetY / this.containerZoom.offsetHeight * 100;
        }
    }

    /**
     * @type {HTMLElement} container
     * @type {TouchEvent} event
     * @private
     */
    _zoomMoveEventMobile(event) {

        const offsetX = (event.touches[0].pageX - this.positionX) * this.options.deltaMove;
        const offsetY = (event.touches[0].pageY - this.positionY) * this.options.deltaMove;

        const positionPictureX = this.pctPositionX / 100 * this.containerZoom.offsetWidth;
        const positionPictureY = this.pctPositionY / 100 * this.containerZoom.offsetHeight;

        this._setCenterPicture(
            (this.options.inverted ? positionPictureX + offsetX : positionPictureX - offsetX),
            (this.options.inverted ? positionPictureY + offsetY : positionPictureY - offsetY)
        );
        this.positionX = event.touches[0].pageX;
        this.positionY = event.touches[0].pageY;
        this._update();
    }

    /**
     * @param {TouchEvent} event
     * @private
     */
    _zoomPinchEvent(event) {
        const curDiff = Math.hypot(
            event.touches[0].pageX - event.touches[1].pageX,
            event.touches[0].pageY - event.touches[1].pageY
        ) * 0.03;

        let sizeContainer = this.sizeContainer;
        if (!sizeContainer) {
            sizeContainer = Math.sqrt(
                Math.pow(this.containerZoom.offsetWidth, 2) +
                Math.pow(this.containerZoom.offsetHeight, 2)
            );
            this.sizeContainer = sizeContainer;
        }

        if (!this.sizeContainer) {
            return;
        }

        if (this.lastZoomPinch > 0) {
            this._setCenterPicture(
                this.centerViewPinch.x,
                this.centerViewPinch.y
            );
            if (this.lastZoomPinch > curDiff) {
                this.setZoom(this._currentZoom - ((curDiff / this.sizeContainer) * this.options.zoomMax));
            } else {
                this.setZoom(this._currentZoom + ((curDiff / this.sizeContainer) * this.options.zoomMax));
            }
            this.lastZoomPinch = curDiff;
        } else {
            let centerViewX = (event.touches[0].pageX + event.touches[1].pageX) / 2;
            let centerViewY = (event.touches[0].pageY + event.touches[1].pageY) / 2;
            this.centerViewPinch = {
                x: centerViewX,
                y: centerViewY
            };
            this._setCenterPicture(
                centerViewX,
                centerViewY
            );
            this.lastZoomPinch = curDiff;
        }
    }

    get pctPositionX() {
        return this._pctPositionX;
    }

    set pctPositionX(positionX) {
        this._pctPositionX = Math.max(Math.min(positionX, 100), 0);
    }

    get pctPositionY() {
        return this._pctPositionY;
    }

    set pctPositionY(positionY) {
        this._pctPositionY = Math.max(Math.min(positionY, 100), 0);
    }

    get positionX() {
        return this._positionX;
    }

    set positionX(positionX) {
        this._positionX = positionX;
    }

    get positionY() {
        return this._positionY;
    }

    set positionY(positionY) {
        this._positionY = positionY;
    }

    get currentZoom() {
        return this._currentZoom;
    }
}
